import { Avatar } from '@DesignSystem/data-display';
import { IconButton, Tooltip } from '@ds';
import { DSMoreBreadcrumbsIcon } from '@ds-icons';
import React, { useState } from 'react';
import { Popper } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import typography from '@ds-typography';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import isEmpty from 'lodash/isEmpty';
import styles from './AdminList.module.scss';
import { useRefSizes } from '@shared/hooks';
import { useOrganizationUsersByRole } from '@account-settings';
import Skeleton from '@mui/material/Skeleton';
import { OrganizationUserRole } from '@shared/types';

const ADMIN_ITEM_WIDTH = 120; // Approximate width per admin item

export default function AdminList() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { refWidth = 0, sizeRef } = useRefSizes();

  const {
    data: admins = [],
    isLoading: isLoadingAdmins
  } = useOrganizationUsersByRole(OrganizationUserRole.ADMIN);

  if (isLoadingAdmins)
    return (
      <Skeleton
        variant="rectangular"
        style={{ borderRadius: 5 }}
        width="100%"
        height="30px"
      />
    );

  const handleOpenModal = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const maxVisibleAdmins = Math.floor(refWidth / ADMIN_ITEM_WIDTH);
  const visibleAdmins = admins.slice(0, maxVisibleAdmins);
  const extraAdminsList = admins.slice(maxVisibleAdmins);

  return (
    <div className={styles.adminListContainer} ref={sizeRef}>
      {visibleAdmins.map(admin => {
        const { userId, username, avatar, email } = admin;

        if (isEmpty(username)) return null;

        return (
          <div key={userId} className={styles.adminListItem}>
            <Avatar src={avatar} width="20px" />
            <Tooltip
              interactive
              content={
                <div className={styles.tooltipContent}>
                  <p className={typography.dsBodySmall}>{username}</p>
                  <p className={typography.dsBodySmall}>{email}</p>
                </div>
              }
            >
              <p className={typography.dsBody}>{username}</p>
            </Tooltip>
          </div>
        );
      })}

      {!isEmpty(extraAdminsList) && (
        <IconButton
          onClick={handleOpenModal}
          type="secondary"
          Icon={<DSMoreBreadcrumbsIcon />}
        />
      )}

      <Popper
        placement="bottom-end"
        style={{ zIndex: 0 }}
        open={!!anchorEl}
        anchorEl={anchorEl}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper className={styles.adminPopperPaper} elevation={0}>
            {extraAdminsList.map(admin => {
              const { userId, username, avatar, email } = admin;

              if (isEmpty(username)) return null;

              return (
                <div key={userId} className={styles.adminPopperContainer}>
                  <Avatar src={avatar} width="32px" />
                  <div className={styles.adminPoppperInfo}>
                    <p className={typography.dsBody}>{username}</p>
                    <p className={typography.dsBodySmall}>{email}</p>
                  </div>
                </div>
              );
            })}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </div>
  );
}
